// @flow
import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useStore, useSelector, useDispatch } from 'react-redux';
import { dashboardPageUrl } from '@context/user';
import { emptyFilesToast } from '@context/toasts';
import { useRecordEditorOn } from '@helpers/connect';
import { memedPrescriptionItem } from './memedPrescriptionItem';
import type { PDFDoc } from '../../elements/PDFPreview/PDFPreview';
import type
{
  Record as RecordType,
  DrugsPrescriptionsState,
  ExamsPrescriptionsState,
  DocumentsState,
  CustomFormsState,
  Patient,
  AttachmentsState,
  CustomFormTemplatesState,
  Attachment,
  PlanInclude,
} from '@types';
import {
  get, map, pick, size, keys, forEach, debounce, isEqual, filter, findIndex,
} from 'lodash';
import { useIsMobile, useTranslationNs, fsPrintf, isPresent, replaceHtmlEntitesToText, handleMomenti18n, UserNotifications, sort, numberCompare, fileExtensionIsExecutable, isCordovaApp, isShareable, isPdfFile, normalizeHeicFile, readsEmpty } from '@helpers/helpers';
import moment from 'moment';

import { Dropdown, ModalClickableList, Button, CustomDayPicker, Icon, DragAndDropFile, Spinner, TextArea } from '@elements';
import classNames from 'classnames';
import { VisibilityLevelIcon, RecordDateHeader } from './mixins';
import RecordDetails from './RecordDetails/RecordDetails';
import RecordTextField, { preloadRichTextEditor } from './RecordText/RecordTextField';
import './Styles/FileAttachment.scss';
import './Styles/Record.scss';
import './Styles/RecordBlocked.scss';
import './Styles/RecordCanceled.scss';
import './Styles/RecordCard.scss';
import './Styles/RecordDetails.scss';
import './Styles/RecordPrescription.scss';
import './Styles/MobileExcluise.scss';


type AttachmentActionItem = {
  text: string,
  action: string,
};

type VisibilityLevelItemType = {
  level: number,
  text: string,
  rightIcon?: string,
  leftIcon?: string,
  disabled?: boolean,
};

type Props = {
    onDirectShare: (recordId: number, resource: string, resourceId: number, respondWithUrls: (wtt: string | null, mail: string | null) => void) => void,
    attachments: AttachmentsState,
    onPrintDocumentClick?: Function,
    onPrintPrescriptionDrugsClick?: Function,
    onPrintPrescriptionExamsClick: Function,
    onPrintCustomFormClick?: Function,
    record: RecordType,
    patient: Patient,
    clinicId: number,
    userEmail: string,
    drugsPrescriptions: DrugsPrescriptionsState,
    examsPrescriptions: ExamsPrescriptionsState,
    documents: DocumentsState,
    customForms: CustomFormsState,
    onChangeVisibilityLevel: Function,
    onDeleteRecord: Function,
    onSelectFile: Function,
    onPrintSingleRecord: Function,
    onPrintAllRecords: Function,
    onDeleteAttachment: Function,
    onToggleEditMode: Function,
    onUpdateRecordDate: Function,
    customFormTemplates: CustomFormTemplatesState,
    mainSection: any,
    onNewDocumentClick: Function,
    onNewPrescriptionDrugsClick: Function,
    onNewPrescriptionExamsClick: Function,
    onNewCustomFormClick: Function,
    onDisplayAttachmentFileName: Function,
    // Opens the PDF viewer on the record's PDFs, at `index`
    showPDF: (docs: Array<PDFDoc>, index: number) => void,
    isEdit: boolean,
    // Edit
    planInclude?: PlanInclude,
    singleUser?: boolean,
    experimentalFeatures: boolean,
    onEditCustomFormClick?: Function,
    onEditPrescriptionDrugsClick?: Function,
    onEditPrescriptionExamsClick?: Function,
    onEditDocumentClick?: Function,
    onDownloadAttachment: Function,
    isDeleting?: boolean,
    isNew?: boolean,
    isSaving?: boolean,
    isAutoSaving?: boolean,
    onSaveRecord?: Function,
    onAutosaveRecord?: Function,
};

const presentRecordDate = (record: any) => isPresent(get(record, 'date'));

const limitName = (name: string, limit = 50) => (String(name).trim().length > limit ? `${String(name).substr(0, limit).trim()}...` : name);

const RecordElement = ({
  record, onPrintDocumentClick, onSelectFile, onPrintSingleRecord, onPrintPrescriptionExamsClick, onPrintPrescriptionDrugsClick,
  attachments, onPrintCustomFormClick, onPrintAllRecords, onNewPrescriptionExamsClick, onNewPrescriptionDrugsClick,
  customFormTemplates, onNewDocumentClick, onNewCustomFormClick, onDeleteRecord, onDeleteAttachment, onChangeVisibilityLevel,
  customForms, mainSection, examsPrescriptions, drugsPrescriptions, documents, patient, clinicId, userEmail,
  onToggleEditMode, showPDF, isEdit, planInclude,
  onEditCustomFormClick, onEditPrescriptionDrugsClick, onEditPrescriptionExamsClick, onEditDocumentClick, isDeleting,
  isSaving, isAutoSaving, onSaveRecord, onAutosaveRecord, isNew, singleUser, onDownloadAttachment,
  onUpdateRecordDate, onDirectShare, experimentalFeatures,
}: Props) => {
  const [isMobile, isIpad] = useIsMobile();
  const store = useStore();
  const dispatch = useDispatch();
  const accessToken = store.getState().auth.token;
  const systemDialogEl = useRef(null);
  // The record's text while it is being written, as dev's textarea has it: from the record when this element mounts,
  // then what was typed, kept from one session to the next; finalizing sends it. Without the editor (no access, or
  // turned off) that is all there is, exactly as on dev.
  const contentText = useRef(replaceHtmlEntitesToText(get(record, ['details', 'content'], '') || ''));
  // With the editor on (in Ajustes, for a doctor: useRecordEditorOn), each session (the record opened for writing)
  // starts from the record as it is then (it may have changed since): savedContent the text as saved, which finalizing
  // sends while nothing was typed in the editor; contentText its textarea copy (entities and line breaks as text).
  // textSource is the editor's reader (it reads its text only when asked: the draft's debounce, finalizing, leaving);
  // edited, something was typed this session.
  const editorEnabled = useRecordEditorOn();
  const editorOn = useRef(false);
  editorOn.current = editorEnabled;
  const savedContent = useRef('');
  const edited = useRef(false);
  const textSource = useRef(null);
  const lockField = useRef(null);
  const writing = useRef(false);
  if (isEdit && !writing.current) {
    const saved = get(record, ['details', 'content'], '') || '';
    savedContent.current = saved;
    edited.current = false;
    textSource.current = null;
    if (editorOn.current) contentText.current = replaceHtmlEntitesToText(saved);
  }
  writing.current = isEdit;
  // What was typed this session is a textarea's text (dev's, or the editor's field's): shown as it is, never decoded
  // again, when the field changes under it (the editor turned on or off in Ajustes)
  const typedInTextarea = () => edited.current && !textSource.current;
  const textareaText = () => (typedInTextarea() ? contentText.current : replaceHtmlEntitesToText(contentText.current || ''));

  // The record's text as it stands: what was typed (read from the editor now); with the editor on and nothing typed,
  // the text exactly as saved
  const currentText = useCallback((): string => {
    if (textSource.current) {
      try {
        contentText.current = textSource.current();
      } catch (error) {
        // The text as last read
      }
    }
    if (!edited.current && editorOn.current) return savedContent.current;
    return contentText.current;
  }, []);

  const [autoSave, setAutoSave] = useState(false);
  const [isDaypickerOpen, setIsDaypickerOpen] = useState(false);
  const [showMobileContentMenu, setShowMobileContentMenu] = useState(false);
  const [selectedDate, setSelectedDate] = useState(presentRecordDate(record) ? moment(get(record, ['date'])) : moment());
  const [tempSelectedDate, setTempSelectedDate] = useState(presentRecordDate(record) ? moment(get(record, ['date'])) : moment());

  const tplanInclude = planInclude || {};

  useEffect(() => {
    setIsDaypickerOpen(false);
    setShowMobileContentMenu(false);
    setSelectedDate(presentRecordDate(record) ? moment(get(record, 'date')) : moment());
    setTempSelectedDate(presentRecordDate(record) ? moment(get(record, 'date')) : moment());
    contentText.current = replaceHtmlEntitesToText(get(record, ['details', 'content'], ''));
    return () => {};
    // eslint-disable-next-line
  }, []);

  // The editor's code loads with the timeline, before a record is opened
  useEffect(() => {
    if (editorEnabled) preloadRichTextEditor();
  }, [editorEnabled]);

  useEffect(() => () => {
    if (autoSave) {
      setAutoSave(false);
    }
  }, [isSaving, autoSave]);

  // ========================== Basic initializations and vars ==========================
  const [_, i18n] = useTranslationNs('timeline');

  handleMomenti18n(i18n, moment);

  const appointmentNote = useMemo(() => get(record, ['appointmentData', 'appointmentNotes']), [record]);
  const detailsContent = useMemo(() => get(record, ['details', 'content']), [record]);
  const visibilityLevelIsEditable = useMemo(() => get(record, ['details', 'visibilityLevelIsEditable']), [record]);
  const permissionClasses = useMemo(() => (visibilityLevelIsEditable ? 'permission' : 'permission disabled'), [visibilityLevelIsEditable]);

  const selectedVisibility = useCallback((level: number) =>
    (parseInt(level, 10) === parseInt(get(record, 'visibilityLevel'), 10) ? 'selectedVisibilityLevelItem' : ''), [record]);

  // ========================== Details Variables ==========================

  // retrive only those drugs prescriptions which are associated with this particular record
  const drugsPrescriptionsIds = useMemo(() => get(record, ['details', 'drugsPrescriptions'], []), [record]);
  const drugsPrescriptionsContent = useMemo(() => drugsPrescriptions && drugsPrescriptions.data && pick(drugsPrescriptions.data, drugsPrescriptionsIds), [drugsPrescriptions, drugsPrescriptionsIds]);

  // retrive only those exams prescriptions which are associated with this particular record
  const examsPrescriptionsIds = useMemo(() => get(record, ['details', 'examsPrescriptions'], []), [record]);
  const examsPrescriptionsContent = useMemo(() => examsPrescriptions && examsPrescriptions.data && pick(examsPrescriptions.data, examsPrescriptionsIds), [examsPrescriptions, examsPrescriptionsIds]);

  // retrive only those documents which are associated with this particular record
  const documentsIds = useMemo(() => get(record, ['details', 'documents'], []), [record]);
  const documentsContent = useMemo(() => documents && documents.data && pick(documents.data, documentsIds), [documents, documentsIds]);

  // retrive only those custom forms which are associated with this particular record
  const customFormsIds = useMemo(() => get(record, ['details', 'customForms'], []), [record]);
  const customFormsContent = useMemo(() => customForms && customForms.data && pick(customForms.data, customFormsIds), [customFormsIds, customForms]);

  const recordFilesIds = useMemo(() => get(record, ['details', 'fileAttachments'], []), [record]);
  const recordFiles = useMemo(() => map(recordFilesIds, (recordId) => (
    get(attachments, ['data', recordId], {})
  )), [recordFilesIds, attachments]);

  const tempFiles = useMemo(() => get(record, ['tempFiles', 'fileAttachments']), [record]);
  const showFileAttachments = useMemo(() => size(tempFiles) > 0 || recordFilesIds.length > 0, [tempFiles, recordFilesIds]);

  const fileAttachmentDropdownList = useMemo(() => [
    ...(window.isCordovaApp ? [{ text: _('action/share'), action: 'download' }] : []),
    ...(isEdit ? [{ text: 'Deletar', action: 'delete' }] : []),
  ], [_, isEdit]);

  // ========================== Images handling ==========================
  const recordImagesIds = useMemo(() => get(record, ['details', 'imageAttachments'], []), [record]);

  const recordImages = useMemo(() => sort(map(recordImagesIds, (recordId) => (
    get(attachments, ['data', recordId], {})
  )), numberCompare('id')), [attachments, recordImagesIds]);

  const tempImages = useMemo(() => get(record, ['tempFiles', 'imageAttachments']), [record]);
  const showImageAttachments = useMemo(() => size(tempImages) > 0 || recordImagesIds.length > 0, [tempImages, recordImagesIds]);

  const handleDownloadAttachment = useCallback((attachmentId: number, callback: Function = () => {}, disableShare: boolean = false) => {
    onDownloadAttachment(record.id, attachmentId, callback, disableShare);
  }, [onDownloadAttachment, record.id]);

  // ========================== Simplifications ==========================

  const recordSpl = useMemo(() => ({
    isEditable: get(record, ['details', 'isEditable'], false),
    isMine: get(record, ['details', 'isMine'], false),
    highlightEdit: get(record, ['details', 'highlightEdit'], false),
    fromMigration: get(record, 'fromMigration', false),
    actualDate: moment(record.date).format('DD/MM/YYYY'),
    initialDate: moment(record.createdAt).format('DD/MM/YYYY'),
  }), [record]);

  // ========================== Lists ==========================

  const visibilitLevelItems = useMemo(() => [
    {
      text: _('action/visibility/label'), break: true, className: 'headingDropDownRecord', hideMobile: true,
    },
    {
      level: 3, text: _('action/visibility/all'), leftIcon: 'earth', className: selectedVisibility(3),
    },
    {
      level: 2, text: _('action/visibility/only_doctors'), leftIcon: 'account-multiple', className: selectedVisibility(2),
    },
    {
      level: 1, text: _('action/visibility/only_me'), leftIcon: 'lock', className: selectedVisibility(1),
    },
  ], [selectedVisibility, _]);

  const optionsDropdownList = [
    ...(recordSpl.isEditable ? [{ text: _('action/change_date'), action: 'edit-record-date' }] : []),
    { text: _(window.isCordovaApp ? 'action/print_record_share' : 'action/print_record'), action: 'print-single-record', id: record.id },
    // ...(recordSpl.fromMigration ? [] : [{ text: _(window.isCordovaApp ? 'action/print_full_record_share' : 'action/print_full_record'), action: 'print-all-records' }]),
    ...(recordSpl.isMine ? [{ text: _('action/delete_record'), action: 'delete-record', id: record.id }] : []),
  ];

  const templateIds = keys(get(customFormTemplates, 'data'));

  const customFormsDropdownList = map<any, any>(templateIds, (templateId) => {
    const template = get(customFormTemplates, ['data', templateId]);
    return {
      templateId,
      text: limitName(template.name),
      action: 'new-custom-form',
    };
  });

  const memedConnected = useSelector(state => get(state, ['context', 'user', 'advancedData', 'memedConnected']));
  const memedItem = useMemo(() => memedPrescriptionItem({
    text: _('action/content_add/memed'),
    connected: memedConnected,
    memedUrl: dashboardPageUrl(accessToken, `/app/clinics/${clinicId}/patients/${record.patientId}/records/${record.id}/memed_prescriptions/new`),
    patientId: record.patientId,
  }), [_, memedConnected, accessToken, clinicId, record.patientId, record.id]);

  const prescriptionsDropdownList: Object[] = [
    ...(tplanInclude.newMemed ? [memedItem] : []),
    { text: _('action/content_add/receipts'), action: 'new-drugs-prescription' },
    { text: _('action/content_add/exam'), action: 'new-exams-prescription' },
    { text: _('action/content_add/docs'), action: 'new-document' },
  ];

  const customFormHeading: any = useMemo(() => (get(customFormsDropdownList, 'length') > 0
    ? [{ text: _('action/content_add/forms'), action: '', className: 'headingSeparator' }] : []
  ), [customFormsDropdownList, _]);

  const shouldRenderActions = (recordSpl.isEditable && (tplanInclude.customForms || tplanInclude.examsDrugsDocuments || tplanInclude.attachments));

  // Not plan-gated: any editable record in an early-adopters clinic gets the AI transcription button
  const showAiTranscription = (recordSpl.isEditable && experimentalFeatures);

  // iOS answers the file input with its own menu (Fototeca / Tirar Foto / Escolher Arquivo), so a camera
  // item next to it repeats one of those choices. Android's file picker has no camera: it keeps the item.
  // Cordova's own platform id: an iPad's WebView can report navigator.platform as "MacIntel".
  const filePickerHasCamera = useMemo(() => get(window, ['cordova', 'platformId']) === 'ios', []);

  const mobileContentMenuActions = useMemo(() => {
    const items = [];

    if (tplanInclude.attachments) {
      items.push(
        { text: _('action/content_add/attachments'), action: '', className: 'headingSeparator' },
        ...(filePickerHasCamera ? [] : [{ text: _('action/content_add/camera'), action: 'attach_cam' }]),
        { text: _('action/content_add/from_files'), action: 'attach_file' },
      );
    }

    if (tplanInclude.examsDrugsDocuments) {
      items.push(
        { text: _('action/content_add/receipts'), action: '', className: 'headingSeparator' },
      );

      if (tplanInclude.newMemed) {
        items.push(memedItem);
      }

      items.push(
        { text: _('action/content_add/prescription'), action: 'add_prescription' },
        { text: _('action/content_add/exam'), action: 'add_exam' },
        { text: _('action/content_add/docs'), action: 'add_documents' },
      );
    }

    if (tplanInclude.customForms && get(customFormsDropdownList, 'length') > 0) {
      items.push(
        ...customFormHeading,
        ...customFormsDropdownList,
      );
    }

    return items;
  }, [_, customFormHeading, customFormsDropdownList, filePickerHasCamera, tplanInclude.attachments, tplanInclude.customForms, tplanInclude.examsDrugsDocuments, tplanInclude.newMemed, memedItem]);

  // ========================== Callbacks ==========================

  const onGetImageSuccess = useCallback((imageUrl: string) => {
    // cordova-plugin-camera v8 returns a full data URL; v7 returned bare base64.
    const imgSrc = imageUrl.startsWith('data:') ? imageUrl : 'data:image/jpeg;base64,' + imageUrl;

    let file;
    fetch(imgSrc)
      .then(res => res.blob())
      .then(blob => {
        const recordId = get(record, 'id');
        const fileExtension = blob.type.split('/')[1];
        file = new W3C_File([blob], `record_attachment_${Date.now()}.${fileExtension}`, { type: blob.type });

        if (recordId && (blob.type === 'image/png' || blob.type === 'image/jpg' || blob.type === 'image/jpeg' || blob.type === 'image/jfif')) {
          const img = new Image();
          img.src = imgSrc;
          img.onload = () => {
            file.width = img.naturalWidth;
            file.height = img.naturalHeight;
            onSelectFile(recordId, file, 'imageAttachments');
          };
        } else {
          UserNotifications.info('info/file_not_allowed');
        }
      });
  }, [record, onSelectFile]);

  const onGetImageFail = useCallback((message: string) => {
    // eslint-disable-next-line
    console.log('Image fetch failed because: ' + message);
  }, []);

  const onGetImageFromCamera = useCallback(() => {
    navigator.camera.getPicture(onGetImageSuccess, onGetImageFail, {
      quality: 80,
      destinationType: navigator.camera.DestinationType.DATA_URL,
      encodingType: navigator.camera.EncodingType.JPEG,
      targetHeight: 1024,
      targetWidth: 1024,
      correctOrientation: true,
    });
  }, [onGetImageSuccess, onGetImageFail]);

  const hideMobileContentMenuItemMenu = useCallback(() => {
    setShowMobileContentMenu(false);
  }, []);

  const showMobileContentMenuItemMenu = useCallback(() => {
    setShowMobileContentMenu(true);
  }, []);

  const handleOpenSystemDialog = useCallback(() => {
    if (systemDialogEl.current) {
      systemDialogEl.current.click();
    }
  }, [systemDialogEl]);

  const handleMobileContentMenuItemClick = useCallback((item: Object) => {
    switch (item.action) {
      case 'attach_cam': onGetImageFromCamera(); break;
      case 'attach_file': handleOpenSystemDialog(); break;
      case 'add_prescription': onNewPrescriptionDrugsClick({ recordId: record.id, patientName: patient.name }); break;
      case 'add_exam': onNewPrescriptionExamsClick({ recordId: record.id, patientName: patient.name }); break;
      case 'add_documents': onNewDocumentClick({ recordId: record.id }); break;
      case 'new-custom-form': onNewCustomFormClick({ recordId: record.id, templateId: item.templateId }); break;
      default: break;
    }
    hideMobileContentMenuItemMenu();
  }, [
    hideMobileContentMenuItemMenu, onGetImageFromCamera,
    handleOpenSystemDialog, onNewPrescriptionDrugsClick, record.id, patient.name, onNewPrescriptionExamsClick, onNewDocumentClick, onNewCustomFormClick,
  ]);

  const handleOpenPicker = useCallback(() => {
    setIsDaypickerOpen(true);
  }, []);

  const printSimplifiedRecord = useCallback((withSignature: boolean) => () => {
    onPrintSingleRecord(record.id, true, withSignature);
  }, [onPrintSingleRecord, record]);

  const onSelectRecordOption = useCallback((item: Object) => {
    switch (item.action) {
      case 'edit-record-date':
        if (record.appointmentData) {
          UserNotifications.recordFriendlyAlert('info/finalize_record_tip/change_date/with_appointment', 'ban', '#666666');
        } else {
          handleOpenPicker();
        }
        break;
      case 'delete-record':
        UserNotifications.confirmI18n('alert/delete/record', 'warning', '#FFA433', true).then(deleteConfirm => {
          if (deleteConfirm) {
            onDeleteRecord(item.id);
          }
        });
        break;
      case 'print-single-record':
        if (isEdit) {
          UserNotifications.recordFriendlyAlert('info/finalize_record_tip/print');
        } else {
          onPrintSingleRecord(item.id, false);
        }
        break;
      case 'print-single-record-simple':
        if (isEdit) {
          UserNotifications.recordFriendlyAlert('info/finalize_record_tip/print');
        } else {
          onPrintSingleRecord(item.id, true);
        }
        break;
      case 'print-all-records':
        if (isEdit) {
          UserNotifications.recordFriendlyAlert('info/finalize_record_tip/print');
        } else {
          onPrintAllRecords(patient.id);
        }
        break;
      default:
        break;
    }
  }, [record.appointmentData, isEdit, handleOpenPicker, onDeleteRecord, onPrintSingleRecord, onPrintAllRecords, patient.id]);

  const handleSelectDate = useCallback((date: string) => {
    const day = moment(date).date();
    const month = moment(date).month();
    const year = moment(date).year();
    setTempSelectedDate(moment([year, month, day]));
  }, []);

  const handleCancelDayPicker = useCallback(() => {
    setTempSelectedDate(selectedDate);
    setIsDaypickerOpen(false);
  }, [selectedDate]);

  const handleAcceptDayPicker = useCallback(() => {
    onUpdateRecordDate(record.id, tempSelectedDate.toISOString());

    setSelectedDate(tempSelectedDate);
    setIsDaypickerOpen(false);

    return tempSelectedDate;
  }, [record, tempSelectedDate, onUpdateRecordDate]);

  const onStartButtonClick = useCallback(() => {
    onToggleEditMode(get(record, 'id'), true);
  }, [onToggleEditMode, record]);

  const handleDeleteAttachment = useCallback((attachmentId: number, disableNotification: boolean = false) => () => {
    if (!disableNotification) {
      UserNotifications.confirmI18n('alert/confirm_delete_file', 'warning', '#FFA433', true)
        .then((value) => {
          if (value) {
            onDeleteAttachment(record.id, attachmentId);
          }
        });
    } else {
      onDeleteAttachment(record.id, attachmentId);
    }
  }, [onDeleteAttachment, record.id]);

  const onSelectFileAction = useCallback((file: Attachment) => (item: AttachmentActionItem) => {
    if (item.action === 'share') {
      handleDownloadAttachment(file.id, (downloadUrl) => {
        if ((downloadUrl && !isCordovaApp()) || (downloadUrl && isShareable(file.name))) {
          const win = window.open(downloadUrl, '_blank');
          if (win) {
            win.focus();
          }
        }
      });
    }

    if (item.action === 'delete') {
      UserNotifications.confirmI18n('alert/confirm_delete_file', 'warning', '#FFA433', true)
        .then((value) => {
          if (value) {
            onDeleteAttachment(record.id, file.id);
          }
        });
    }
  }, [onDeleteAttachment, record.id, handleDownloadAttachment]);

  // The record's PDFs, one after the other in the viewer. The apps open each with a fresh signed URL and share it
  // from there, as their file menu does; the web opens the URL the record came with, and a fresh one on a retry.
  const recordPdfs = useMemo(() => filter(recordFiles, isPdfFile), [recordFiles]);
  const onOpenPDF = useCallback((file: Attachment) => {
    const docs = map(recordPdfs, (pdf: Attachment) => {
      const load = () => new Promise(resolve => handleDownloadAttachment(pdf.id, resolve, true));
      return isCordovaApp()
        ? { key: String(pdf.id), name: pdf.name, load, onDownload: () => handleDownloadAttachment(pdf.id) }
        : { key: String(pdf.id), name: pdf.name, url: pdf.downloadUrl, load };
    });
    showPDF(docs, Math.max(0, findIndex(recordPdfs, pdf => pdf.id === file.id)));
  }, [recordPdfs, handleDownloadAttachment, showPDF]);

  const handlePrintDrugsPrescriptionClick = useCallback((recordId: number, prescriptionId: number, showSignature: boolean, endEmail: boolean) => () => {
    if (onPrintPrescriptionDrugsClick) {
      onPrintPrescriptionDrugsClick(recordId, prescriptionId, showSignature, endEmail);
    }
  }, [onPrintPrescriptionDrugsClick]);

  const handlePrintExamsPrescriptionClick = useCallback((recordId: number, prescriptionId: number, showSignature: boolean, sendEmail: boolean) => () => {
    if (onPrintPrescriptionExamsClick) {
      onPrintPrescriptionExamsClick(recordId, prescriptionId, showSignature, sendEmail);
    }
  }, [onPrintPrescriptionExamsClick]);

  const handlePrintCustomFormClick = useCallback((recordId: number, customFormId: number) => () => {
    if (onPrintCustomFormClick) {
      onPrintCustomFormClick(recordId, customFormId);
    }
  }, [onPrintCustomFormClick]);

  const handlePrintDocumentClick = useCallback((recordId: number, documentId: number, showSignature: boolean, sendEmail: boolean) => () => {
    if (onPrintDocumentClick) {
      onPrintDocumentClick(recordId, documentId, showSignature, sendEmail);
    }
  }, [onPrintDocumentClick]);

  const handleEditCustomFormClick = useCallback((recordId: number, customFormId: number) => () => {
    if (onEditCustomFormClick) {
      onEditCustomFormClick({ recordId, customFormId });
    }
  }, [onEditCustomFormClick]);

  const handleEditDrugsPrescriptionClick = useCallback((recordId: number, prescriptionId: number, patientName: string) => () => {
    if (onEditPrescriptionDrugsClick) {
      onEditPrescriptionDrugsClick({ recordId, prescriptionId, patientName });
    }
  }, [onEditPrescriptionDrugsClick]);

  const handleEditExamsPrescriptionClick = useCallback((recordId: number, prescriptionId: number, patientName: string) => () => {
    if (onEditPrescriptionExamsClick) {
      onEditPrescriptionExamsClick({ recordId, prescriptionId, patientName });
    }
  }, [onEditPrescriptionExamsClick]);

  const handleEditDocumentClick = useCallback((recordId: number, documentId: number) => () => {
    if (onEditDocumentClick) {
      onEditDocumentClick({ recordId, documentId });
    }
  }, [onEditDocumentClick]);

  const handleChangeVisibilityLevel = useCallback((visibilityLevelItem: VisibilityLevelItemType) => {
    onChangeVisibilityLevel(record.id, visibilityLevelItem.level);
  }, [onChangeVisibilityLevel, record.id]);

  const handleDropFiles = useCallback((fileList: Array<Object>) => {
    const sendFile = (file) => {
      // Executables are checked on the original name: renaming first would turn
      // 'x.exe' reported as image/heic into 'x.exe.jpeg' and skip the guard below.
      file = fileExtensionIsExecutable(file.name) ? file : normalizeHeicFile(file);
      if (file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg' || file.type === 'image/jfif') {
        // Its dimensions are read where it is shrunk for the upload (prepareImageForUpload)
        onSelectFile(record.id, file, 'imageAttachments');
      } else if (!fileExtensionIsExecutable(file.name)) {
        onSelectFile(record.id, file, 'fileAttachments');
      } else {
        UserNotifications.info('info/file_not_allowed');
      }
    };
    // An empty file (0 bytes) isn't sent: S3 refuses it and trying again can't help. A size of 0 can also be a file
    // the browser couldn't measure (one an Android app streams), so those are read first: only what reads as empty, or
    // can't be read, is left out. The others go on, and one toast names the empty ones.
    const sizeless = [];
    forEach(fileList, (file) => {
      if (file.size === 0) sizeless.push(file);
      else sendFile(file);
    });
    if (sizeless.length === 0) return;
    Promise.all(map(sizeless, (file) => readsEmpty(file).then((empty) => {
      if (!empty) sendFile(file);
      return empty ? file.name : null;
    }))).then((names) => {
      const emptyNames = names.filter(Boolean);
      if (emptyNames.length > 0) dispatch(emptyFilesToast(emptyNames));
    });
  }, [dispatch, onSelectFile, record.id]);

  const handleSelectFile = useCallback((e: Object) => {
    e.stopPropagation();
    e.preventDefault();
    // Same routing as a drop (iOS camera photos come this way)
    handleDropFiles(e.target.files);
    // Picking the same file again (after deleting it) must fire change again
    e.target.value = '';
  }, [handleDropFiles]);

  const handleActionsDropdownClick = useCallback((item: any) => {
    switch (item.action) {
      case 'new-document':
        onNewDocumentClick({ recordId: record.id });
        break;
      case 'new-drugs-prescription':
        onNewPrescriptionDrugsClick({ recordId: record.id, patientName: patient.name });
        break;
      case 'new-exams-prescription':
        onNewPrescriptionExamsClick({ recordId: record.id, patientName: patient.name });
        break;
      case 'new-custom-form':
        onNewCustomFormClick({ recordId: record.id, templateId: item.templateId });
        break;
      default:
        break;
    }
  }, [onNewDocumentClick, record.id, onNewPrescriptionDrugsClick, patient.name, onNewPrescriptionExamsClick, onNewCustomFormClick]);


  const handleOpenAssistantDoctor = useCallback(() => {
    const assistantDoctorUrl = `https://app.doutorassistente.com.br/doutore_new_transcription?user_email=${userEmail}&chart_id=${patient.id}&patient_name=${patient.name}&patient_sex=${patient.gender || ''}&patient_guardian_name=${patient.guardianName || ''}`;
    window.open(assistantDoctorUrl, '_blank');
  }, [patient, userEmail]);

  // ========================== Renders ==========================
  const actionsRender = () => [
    <div className={classNames('actions', { ipad: isIpad })} key="action1">
      {isEdit && (shouldRenderActions || showAiTranscription) && (
        <>
          {shouldRenderActions && (
            <Dropdown
              items={[]}
              placeholder='Adicionar Conteúdo'
              className={isIpad ? 'add-content-action ipad-add-icon' : 'add-content-action'}
              icon='add-circle-outline'
              onClick={showMobileContentMenuItemMenu}
            />
          )}
          {showAiTranscription && (
            <Button
              onClick={handleOpenAssistantDoctor}
              text="Transcrição por IA"
              className="assistant-doctor-button"
            />
          )}
        </>
      )}
      {showMobileContentMenu &&
        <ModalClickableList
          items={mobileContentMenuActions}
          onListItemClick={handleMobileContentMenuItemClick}
          onClose={hideMobileContentMenuItemMenu}
        />
      }
    </div>,
    <div className={classNames('right record-add-action', { ipad2: isIpad })} key="action2">
      { !isMobile && (recordSpl.isEditable && !recordSpl.fromMigration) && <EditButton /> }
      <Dropdown
        className="recordOptions"
        placeholder={_('label/options', 'global')}
        items={optionsDropdownList}
        onSelectItem={onSelectRecordOption}
      />
    </div>];

  const EditButton = ({ className }: Object) => (recordSpl.highlightEdit ? null : (
    <div className={classNames('action', className)}>
      <Button onClick={onStartButtonClick}>
        <Icon iconName="edit" className="icon" size={15} />
        <span>{_('action/edit', 'global')}</span>
      </Button>
    </div>
  ));

  // The draft: what is typed goes to the server a second after the last keystroke. The status next to
  // "Finalizar consulta" comes from the answer to each request, never from comparing texts.
  const latest = useRef({});
  latest.current = { record, isSaving, isEdit, isDeleting, onAutosaveRecord };
  // Something typed that no draft has sent yet
  const pendingDraft = useRef(false);
  const draftRequest = useRef(null);
  const finalizing = useRef(false);
  const mounted = useRef(true);
  const draftStatusRef = useRef(null);
  const [draftStatus, setDraftStatus] = useState(null); // 'saving' | 'saved' | 'failed'
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const showDraftStatus = useCallback((status: ?string) => {
    draftStatusRef.current = status;
    if (mounted.current) setDraftStatus(status);
  }, []);

  const sendDraft = useCallback((value: string) => {
    const { record: current, onAutosaveRecord: send } = latest.current;
    if (!send) return;
    pendingDraft.current = false;
    const request = Promise.resolve(send({ ...current, content_draft: value, visibility_level: get(current, 'visibilityLevel') }));
    draftRequest.current = request;
    request.then((saved) => {
      if (draftRequest.current !== request) return;
      draftRequest.current = null;
      if (!mounted.current || !latest.current.isEdit) return;
      if (saved === false) {
        showDraftStatus('failed');
      } else if (!pendingDraft.current) {
        showDraftStatus('saved');
        setDraftSavedAt(moment().format('HH:mm'));
      }
    });
  }, [showDraftStatus]);

  // One debounce for the life of the record. When it fires it reads the latest props and the text as it is then: a
  // finalized or closed record gets no draft, and a record still being saved gets it once that save is back.
  const debouceAutoSave = useRef(null);
  if (!debouceAutoSave.current) {
    debouceAutoSave.current = debounce(() => {
      if (finalizing.current || !latest.current.isEdit) return;
      if (latest.current.isSaving) {
        debouceAutoSave.current();
        return;
      }
      sendDraft(currentText());
    }, 1000);
  }

  // Leaving the record (another patient, the app closing it) sends what is still waiting
  useEffect(() => () => {
    mounted.current = false;
    debouceAutoSave.current.cancel();
    const { isEdit: editing, isDeleting: deleting } = latest.current;
    if (pendingDraft.current && editing && !deleting && !finalizing.current) sendDraft(currentText());
  }, [currentText, sendDraft]);

  // Finalized or closed: this draft is over
  useEffect(() => {
    if (!isEdit) {
      debouceAutoSave.current.cancel();
      pendingDraft.current = false;
      showDraftStatus(null);
    }
  }, [isEdit, showDraftStatus]);

  const handleSaveRecord = useCallback(() => {
    // Nothing more goes into the text from the click on: what is sent is what it shows (the editor now, after
    // reading what was still on its way in; the textarea with the render that follows)
    if (editorOn.current && lockField.current) lockField.current();
    // A draft still waiting would land after the finalized record and bring the draft back
    debouceAutoSave.current.cancel();
    pendingDraft.current = false;
    finalizing.current = true;
    setIsFinalizing(true);
    const text = currentText();
    const tmpRecord = {
      ...record,
      content: text,
      content_draft: null,
      visibility_level: get(record, 'visibilityLevel'),
    };

    Promise.resolve(onSaveRecord && onSaveRecord(tmpRecord)).then((saved) => {
      finalizing.current = false;
      if (mounted.current) setIsFinalizing(false);
      // Not finalized: keep the text as a draft, as it stands now
      if (saved === false && mounted.current && latest.current.isEdit) sendDraft(currentText());
    });
    setAutoSave(true);
  }, [currentText, onSaveRecord, record, sendDraft]);

  // A change in the field: the textarea's text, or the editor's reader of its text
  const handleAutosaveContentText = useCallback((data: { value: string | (() => string) }) => {
    const { value } = data;
    edited.current = true;
    if (typeof value === 'function') {
      textSource.current = value;
    } else {
      textSource.current = null;
      contentText.current = value;
    }
    pendingDraft.current = true;
    if (draftStatusRef.current !== 'saving') showDraftStatus('saving');
    debouceAutoSave.current();
  }, [showDraftStatus]);

  // What the editor's field opens with, read when it opens (a new session, the editor turned on in Ajustes): the text
  // as it stands, and the textarea's copy of it. Turned off, reading it brings contentText up to date with what was
  // written in the editor, for the textarea that follows.
  const field = useMemo(() => {
    if (!isEdit) return null;
    const content = currentText();
    if (!editorEnabled) return null;
    return { content, textarea: textareaText() };
  }, [isEdit, editorEnabled, currentText]); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================== Details Props ==========================

  const detailsCardProps = {
    appointmentNote,
    detailsContent,
    customFormsContent,
    drugsPrescriptionsContent,
    showFileAttachments,
    tempFiles,
    recordFiles,
    fileAttachmentDropdownList,
    showImageAttachments,
    examsPrescriptionsContent,
    documentsContent,
    record,
    tempImages,
    recordImages,
    onSelectFileAction,
    onPrintCustomFormClick: handlePrintCustomFormClick,
    onPrintDrugsPrescriptionClick: handlePrintDrugsPrescriptionClick,
    onPrintDocumentClick: handlePrintDocumentClick,
    onPrintExamsPrescriptionClick: handlePrintExamsPrescriptionClick,
    mainSection,
    onOpenPDF,
    onDirectShare,
    isMine: recordSpl.isMine,
    canDeleteAttachment: recordSpl.isEditable,
    patient,
  };

  const detailsEditProps = {
    isMine: recordSpl.isMine,
    appointmentNote,
    customFormsContent,
    drugsPrescriptionsContent,
    showFileAttachments,
    tempFiles,
    recordFiles,
    fileAttachmentDropdownList,
    showImageAttachments,
    examsPrescriptionsContent,
    documentsContent,
    record,
    tempImages,
    recordImages,
    onSelectFileAction,
    onPrintCustomFormClick: handlePrintCustomFormClick,
    onPrintDrugsPrescriptionClick: handlePrintDrugsPrescriptionClick,
    onPrintDocumentClick: handlePrintDocumentClick,
    onPrintExamsPrescriptionClick: handlePrintExamsPrescriptionClick,
    isEditable: recordSpl.isEditable,
    onEditCustomFormClick: handleEditCustomFormClick,
    patient,
    onEditDrugsPrescriptionClick: handleEditDrugsPrescriptionClick,
    onEditExamsPrescriptionClick: handleEditExamsPrescriptionClick,
    onEditDocumentClick: handleEditDocumentClick,
    planInclude,
    mainSection,
    onOpenPDF,
    canDeleteAttachment: recordSpl.isEditable,
    onDirectShare,
  };

  // ========================== Content ==========================

  return (
    <div className={classNames('register', { openRegister: isEdit, 'deleting-register': isDeleting, isNewRecord: isNew })}>
      <div style={{ display: 'none' }} id={`recordAnchor-${record.id}`} />
      <input
        id="systemDialogEl"
        type="file"
        multiple
        ref={systemDialogEl}
        style={{ display: 'none' }}
        onChange={handleSelectFile}
      />
      {!singleUser && (
        <div className="doctorTabHolder">
          <div className="doctorTab">
            <span>{record.userName}</span>
            <span className={permissionClasses}>
              <VisibilityLevelIcon visibilityLevel={get(record, 'visibilityLevel')} />
              {visibilityLevelIsEditable && (
                <Dropdown
                  usePortal
                  mobileLabel={_('action/visibility/label')}
                  className="visibilityLevelDropdown"
                  items={visibilitLevelItems}
                  showSelected={false}
                  onSelectItem={handleChangeVisibilityLevel}
                />
              )}
            </span>
          </div>
        </div>
      )}
      <DragAndDropFile onDropFiles={handleDropFiles} disabled={!isEdit || !tplanInclude.attachments}>
        <div className="registerTop">
          <RecordDateHeader
            actualDate={recordSpl.actualDate}
            fromMigration={recordSpl.fromMigration}
            initialDate={recordSpl.initialDate}
            record={record}
          />
          {!isEdit && (
            <React.Fragment>
              {isMobile ? actionsRender().reverse() : actionsRender()}
            </React.Fragment>
          )}

          {isEdit && !isMobile && (
            <div className="right">
              {(shouldRenderActions || showAiTranscription) &&
              <div className="actions">
                <label>Adicionar:</label>
                {tplanInclude.attachments &&
                <Button
                  text={_('label/attachment')}
                  onClick={isDeleting ? (() => {}) : handleOpenSystemDialog}
                />
                    }
                {tplanInclude.examsDrugsDocuments &&
                <Dropdown
                  className="prescriptionsDropdown"
                  placeholder={
                    <span className="prescriptionDropdownPlaceholder">
                      {_('label/receipts')}
                    </span>
                  }
                  items={isDeleting ? [] : prescriptionsDropdownList}
                  onSelectItem={handleActionsDropdownClick}
                />
                    }
                {tplanInclude.customForms && get(customFormsDropdownList, 'length') > 0 && (
                <Dropdown
                  className="customFormsDropdown"
                  placeholder={_('label/forms')}
                  items={isDeleting ? [] : customFormsDropdownList}
                  onSelectItem={handleActionsDropdownClick}
                />
                )}
                {showAiTranscription && (
                  <Button
                    onClick={handleOpenAssistantDoctor}
                    text="Transcrição por IA"
                    className="assistant-doctor-button"
                  />
                )}
              </div>
                }
              <Dropdown
                className="recordOptions"
                placeholder={_('label/options', 'global')}
                items={isDeleting ? [] : optionsDropdownList}
                onSelectItem={isDeleting ? (() => {}) : onSelectRecordOption}
              />
            </div>
          )}
          {isMobile && isEdit && (
            <React.Fragment>
              {isMobile ? actionsRender().reverse() : actionsRender()}
            </React.Fragment>
          )}
        </div>

        { isMobile && (recordSpl.isEditable && !recordSpl.fromMigration) && !isEdit && <EditButton className="fill-width-edit-btn" />}

        <div className={classNames('registerBody', { editMode: isEdit })}>

          {!isEdit && recordSpl.highlightEdit && (
            <React.Fragment>
              <Button className="startAppointmentBtn primary" text={_('action/start_treatment')} onClick={onStartButtonClick} />
              {/* {!isEmpty(appointmentNote) && (
                <div className="record freeText">
                  <span className="recordTitle">{_('label/appointment_note')}</span>
                  <div className="recordContent">
                    <p>{parseHtml(appointmentNote)}</p>
                  </div>
                </div>
              )} */}
            </React.Fragment>
          )}

          {isEdit && (
            <div className='record recordTextContainer'>
              <div className='recordContent'>
                {editorEnabled ? (
                  <RecordTextField
                    name="contentText"
                    content={field ? field.content : ''}
                    textareaValue={field ? field.textarea : ''}
                    readOnly={isDeleting || isFinalizing}
                    placeholder={_('label/write_annotations')}
                    onChange={handleAutosaveContentText}
                    lockRef={lockField}
                    _={_}
                  />
                ) : (
                  // Without the editor: the textarea, exactly as it always was (a session's text typed in the editor's
                  // textarea, before the editor was turned off, as it was typed)
                  <TextArea
                    name="contentText"
                    readOnly={isDeleting || isFinalizing}
                    placeholder={_('label/write_annotations')}
                    onChange={handleAutosaveContentText}
                    defaultValue={typedInTextarea() ? contentText.current : replaceHtmlEntitesToText(contentText.current || '')}
                    uncontrolled
                  />
                )}
              </div>
            </div>
          )}

          {isEdit && (
            <div className="recordSaveAction">
              <div className='actions'>
                {draftStatus === 'saving' && <Spinner text={_('label/draft_saving')} />}
                {draftStatus === 'failed' && (
                  <span className="draftStatus failed">
                    <Icon iconName="warning" size={16} />
                    {_('label/draft_save_failed')}
                  </span>
                )}
                {draftStatus === 'saved' && draftSavedAt && (
                  <span className="draftStatus saved">
                    <Icon iconName="check-circle" size={16} />
                    {_('label/draft_saved_at', null, fsPrintf(draftSavedAt))}
                  </span>
                )}
                <Button
                  text={_('action/finalize_appointment')}
                  className='primary'
                  disabled={isSaving || isAutoSaving}
                  onClick={handleSaveRecord}
                />
              </div>
            </div>
          )}

          { (record.detailsVisible || isEdit) && (
            <RecordDetails
              {...(isEdit ? detailsEditProps : detailsCardProps)}
              handleDeleteAttachment={handleDeleteAttachment}
              handleDownloadAttachment={handleDownloadAttachment}
              onPrintSimplifiedRecord={printSimplifiedRecord}
            />
          )}
        </div>
        <CustomDayPicker
          withOverlay
          showButtons
          advancedMode
          isOpen={isDaypickerOpen}
          onSelectDay={handleSelectDate}
          onMonthChange={handleSelectDate}
          selectedDate={tempSelectedDate || moment()}
          onCancel={handleCancelDayPicker}
          onAccept={handleAcceptDayPicker}
        />
      </DragAndDropFile>
    </div>
  );
};


// $FlowFixMe
export default React.memo(RecordElement, (prev, next) => isEqual(prev, next));
